import type { JSX } from 'react';
import { FormGroup } from 'react-bootstrap';
import { OnChangeValue, Props } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { SELECT_MENU_Z_INDEX } from 'Components/FormField/SelectComponentsZIndex';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ColorTheme, OptionType } from 'Models/Types/GlobalType';

import styles from '../FormField.module.css';

interface FormFieldSelectOrCreateProps {
    formFieldId: string;
    formFieldLabel: string;
    handleChange: (value: string | number | undefined, isCreateNew: boolean) => void;
    options: OptionType[];

    /** The type of background that the component will be displayed against. If not supplied, `'light'` will be used. */
    colorTheme?: ColorTheme;
    defaultValue?: OptionType;
    disabled?: boolean;
    required?: boolean;
    tooltip?: string | JSX.Element;
}

/**
 * Similar to FormFieldSelect, but can also be used to define a new value. Displays a drop-down from which the user can select an existing value, or into which the user can type a new value.
 * Use this when the user should be able to populate new choices when filling out a form, such as when creating a third-party service (the service can be for an existing third party, or for a new third party that will also be created on submission of the form).
 */
export const FormFieldSelectOrCreate = (props: FormFieldSelectOrCreateProps) => {
    const colorTheme = props.colorTheme ?? 'light';

    const handleChange = (value: OnChangeValue<OptionType, false>, meta: { action: string }): void => {
        if (value) {
            if (meta.action === 'create-option') {
                props.handleChange(value['value'], true);
            } else {
                props.handleChange(value['value'], false);
            }
        } else {
            props.handleChange(undefined, false);
        }
    };

    const selectProps: Props<OptionType, false> = {
        options: props.options,
        onChange: handleChange,
        isMulti: false,
        isDisabled: props.disabled,
        inputId: props.formFieldId,
        isClearable: true,
        placeholder: 'Select existing or type to create new',
        defaultValue: props.defaultValue,
    };

    return (
        <FormGroup controlId={props.formFieldId} className={styles.formField}>
            <div>
                {props.formFieldLabel && (
                    <FormLabel color={colorTheme === 'light' ? undefined : 'white'} required={props.required}>
                        {props.formFieldLabel}
                    </FormLabel>
                )}
                {props.tooltip && <FormFieldTooltip text={props.tooltip} />}
            </div>
            <CreatableSelect
                {...selectProps}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 9,
                    colors: {
                        ...theme.colors,
                        primary25: '#05263717',
                        text: 'var(--hps-blue)',
                        primary: colorTheme === 'light' ? 'var(--hps-blue)' : 'transparent',
                    },
                })}
                styles={{
                    ...styles,
                    menu: (base) => ({
                        ...base,
                        zIndex: SELECT_MENU_Z_INDEX,
                    }),
                    control: (base) => ({
                        ...base,
                        borderColor: '#053c591a',
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                        backgroundColor: colorTheme === 'light' ? base.backgroundColor : 'var(--hps-blue)',
                    }),
                    multiValueRemove: (styles, { data }) => ({
                        ...styles,
                        color: colorTheme === 'light' ? data.color : 'var(--hps-blue)',
                        ':hover': {
                            color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-dark-blue)',
                        },
                    }),
                    singleValue: (styles) => ({
                        ...styles,
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                    option: (styles, { isSelected }) => ({
                        ...styles,
                        backgroundColor: isSelected ? '#05263717' : '',
                        color: isSelected ? 'var(--hps-blue)' : '',
                    }),
                    input: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                    }),
                }}
            />
        </FormGroup>
    );
};
